import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Trash2, Image as ImageIcon, ChevronUp, ChevronDown, Menu, MapPin, Info } from 'lucide-react';
import api, { adminApi, authApi, fairManagerApi } from '../services/api';
import { FairImage, FairImageCreate, FairImageUpdate } from '../types';
import { confirmAsync, showSuccess, showError } from '../utils/toast';
import Tooltip from './Tooltip';
import { extractApiError } from '../utils/errorUtils'; // Issue #964: Standardized error handling
import { parseDateOnly } from '../utils/fairStatus'; // Issue #1672

// Constants for payment status (Issue #1670)
const PAYMENT_STATUS = {
  PENDING: 'pending' as const,
  PAID: 'paid' as const,
  WAIVED: 'waived' as const
};

type PaymentStatus = typeof PAYMENT_STATUS[keyof typeof PAYMENT_STATUS];

interface FairFormProps {
  isEdit?: boolean;
}

type TabType = 'basic' | 'images';

export default function FairForm({ isEdit = false }: FairFormProps) {
  const { fairId } = useParams<{ fairId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false); // Separate loading state for publish actions
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('basic');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [images, setImages] = useState<FairImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState('');
  const [newImageCaption, setNewImageCaption] = useState('');

  // Separate state for coordinate input strings to allow intermediate values like "-" and "."
  const [latInput, setLatInput] = useState<string>('');
  const [lngInput, setLngInput] = useState<string>('');
  const [geocoding, setGeocoding] = useState(false);
  const [policiesExpanded, setPoliciesExpanded] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    image: '',
    email: '',
    phone: '',
    hours: '',
    website: '',
    // Multi-day scheduling fields (Issue #11)
    start_date: '',
    end_date: '',
    total_slots: 0,
    allows_per_day_reservation: false,
    // Slot offer configuration (Issue #640)
    offer_duration_hours: 48,
    address: {
      street: '',
      city: '',
      state: '',
      zip: '',
      coordinates: {
        lat: 0,
        lng: 0
      }
    },
    social: {
      facebook: '',
      instagram: '',
      twitter: ''
    },
    products: [] as Product[],
    is_test: false,
    is_featured: false,
    is_founding_market: false,
    has_attendant: null as boolean | null,
    // Fair amenities/features (Issue #54)
    has_live_music: false,
    has_alcohol: false,
    has_guest_appearances: false,
    has_giveaways: false,
    has_food_trucks: false,
    is_affiliated: false,
    // Extended operational details (Issue #150)
    daily_hours: '',
    load_in_time: '',
    load_out_time: '',
    parking_info: '',
    is_indoor: false,
    weather_policy: '',
    // Marketing information (Issue #150)
    attendee_entry_fee: 0,
    expected_attendance: 0,
    fair_categories: [] as string[],
    // Vendor requirements (Issue #150)
    insurance_required: false,
    application_deadline: '',
    booth_includes: '',
    power_availability: '',
    // Policies (Issue #150)
    prohibited_items: '',
    sales_tax_info: '',
    photography_policy: '',
    // Publishing status (Issue #1670)
    is_draft: true,
    published_at: null as string | null,
    submission_payment_status: PAYMENT_STATUS.PENDING as PaymentStatus
  });

  useEffect(() => {
    const loadFair = async () => {
      if (!fairId) return;

      try {
        setLoading(true);
        // Use appropriate API based on user role
        const isFairManager = authApi.isFairManager() && !authApi.isAdmin();
        const farmStand = isFairManager
          ? await fairManagerApi.getFairById(Number(fairId))
          : await adminApi.getFairById(Number(fairId));
        setFormData({
          ...farmStand,
          phone: farmStand.phone || '',
          hours: farmStand.hours || '',
          website: farmStand.website || '',
          // Multi-day scheduling fields (Issue #11)
          start_date: farmStand.start_date || '',
          end_date: farmStand.end_date || '',
          total_slots: farmStand.total_slots || 0,
          allows_per_day_reservation: farmStand.allows_per_day_reservation || false,
          // Slot offer configuration (Issue #640)
          offer_duration_hours: farmStand.offer_duration_hours || 48,
          social: {
            facebook: farmStand.social?.facebook || '',
            instagram: farmStand.social?.instagram || '',
            twitter: farmStand.social?.twitter || ''
          },
          is_test: farmStand.is_test || false,
          is_featured: farmStand.is_featured || false,
          is_founding_market: farmStand.is_founding_market || false,
          has_attendant: farmStand.has_attendant ?? null,
          // Fair amenities/features (Issue #54)
          has_live_music: farmStand.has_live_music || false,
          has_alcohol: farmStand.has_alcohol || false,
          has_guest_appearances: farmStand.has_guest_appearances || false,
          has_giveaways: farmStand.has_giveaways || false,
          has_food_trucks: farmStand.has_food_trucks || false,
          is_affiliated: farmStand.is_affiliated || false,
          // Extended operational details (Issue #150)
          daily_hours: farmStand.daily_hours || '',
          load_in_time: farmStand.load_in_time || '',
          load_out_time: farmStand.load_out_time || '',
          parking_info: farmStand.parking_info || '',
          is_indoor: farmStand.is_indoor || false,
          weather_policy: farmStand.weather_policy || '',
          // Marketing information (Issue #150)
          attendee_entry_fee: farmStand.attendee_entry_fee || 0,
          expected_attendance: farmStand.expected_attendance || 0,
          fair_categories: farmStand.fair_categories || [],
          // Vendor requirements (Issue #150)
          insurance_required: farmStand.insurance_required || false,
          application_deadline: farmStand.application_deadline || '',
          booth_includes: farmStand.booth_includes || '',
          power_availability: farmStand.power_availability || '',
          // Policies (Issue #150)
          prohibited_items: farmStand.prohibited_items || '',
          sales_tax_info: farmStand.sales_tax_info || '',
          photography_policy: farmStand.photography_policy || '',
          // Publishing status (Issue #1670)
          is_draft: farmStand.is_draft ?? true,
          published_at: farmStand.published_at || null,
          submission_payment_status: farmStand.submission_payment_status || 'pending'
        });

        // Initialize coordinate input strings from loaded data
        setLatInput(farmStand.address.coordinates.lat?.toString() || '');
        setLngInput(farmStand.address.coordinates.lng?.toString() || '');

        setError(null);
      } catch (err: unknown) {
        console.error('Failed to load artist fair:', err);
        setError('Failed to load artist fair');
      } finally {
        setLoading(false);
      }
    };

    if (isEdit && fairId) {
      loadFair();
    }

    // Load images for editing existing artist fairs
    if (isEdit && fairId) {
      loadImages(Number(fairId));
    }
  }, [isEdit, fairId]);


  const loadImages = async (fairId: number) => {
    try {
      setLoadingImages(true);
      const fairImages = await adminApi.getFairImages(fairId);
      setImages(fairImages);
    } catch (err: unknown) {
      console.error('Failed to load images:', err);
      setError('Failed to load images');
    } finally {
      setLoadingImages(false);
    }
  };

  const deriveAddressFromCoordinates = async () => {
    if (!formData.address.coordinates.lat || !formData.address.coordinates.lng) {
      setError('Please enter latitude and longitude first');
      return;
    }

    setGeocoding(true);
    setError(null);

    try {
      // Use base api instance (axios) directly - has .post() method
      const response = await api.post('/admin/geocode/reverse', null, {
        params: {
          latitude: formData.address.coordinates.lat,
          longitude: formData.address.coordinates.lng
        }
      });

      const addressData = response.data;

      // Auto-fill address fields
      const street = `${addressData.street_number || ''} ${addressData.street || ''}`.trim();
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          street: street || prev.address.street,
          city: addressData.city || prev.address.city,
          state: addressData.state || prev.address.state,
          zip: addressData.zipcode || prev.address.zip
        }
      }));

      // Show success message briefly
      const successMsg = `Address derived: ${addressData.city}, ${addressData.state}`;
      console.log(successMsg);

    } catch (err: unknown) {
      console.error('Geocoding error:', err);
      setError(err.response?.data?.detail || 'Failed to derive address from coordinates');
    } finally {
      setGeocoding(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const isFairManager = authApi.isFairManager() && !authApi.isAdmin();

      // Fix for Issue #1670: Convert empty string dates to null
      const sanitizedData = {
        ...formData,
        application_deadline: formData.application_deadline || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        published_at: formData.published_at || null
      };

      if (isEdit && fairId) {
        if (isFairManager) {
          await fairManagerApi.updateFair(Number(fairId), sanitizedData);
        } else {
          await adminApi.updateFair(Number(fairId), sanitizedData);
        }
      } else {
        await adminApi.createFair(sanitizedData);
      }

      navigate(isFairManager ? '/fairmanager/dashboard' : '/admin/dashboard');
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Error:', apiError);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  // Handler for publishing fair (Issue #1670)
  const handlePublish = async () => {
    if (!fairId) return;

    // Validation: check required fields before publishing
    const missingFields: string[] = [];
    if (!formData.name) missingFields.push('name');
    if (!formData.start_date) missingFields.push('start date');
    if (!formData.end_date) missingFields.push('end date');

    if (missingFields.length > 0) {
      showError(`Please fill in required fields: ${missingFields.join(', ')}`);
      return;
    }

    try {
      setPublishLoading(true);
      const updatedData = {
        ...formData,
        is_draft: false,
        published_at: new Date().toISOString(),
        submission_payment_status: PAYMENT_STATUS.WAIVED
      };

      await adminApi.updateFair(Number(fairId), updatedData);
      setFormData(updatedData);
      showSuccess('Fair published successfully!');
    } catch (err) {
      const apiError = extractApiError(err);
      showError(`Failed to publish fair: ${apiError.message}`);
    } finally {
      setPublishLoading(false);
    }
  };

  // Handler for unpublishing fair (Issue #1670)
  const handleUnpublish = async () => {
    if (!fairId) return;

    const confirmed = await confirmAsync(
      'Are you sure you want to unpublish this fair?',
      {
        description: 'It will no longer be visible to the public.',
        confirmLabel: 'Unpublish',
        cancelLabel: 'Cancel'
      }
    );

    if (!confirmed) return;

    try {
      setPublishLoading(true);
      const updatedData = {
        ...formData,
        is_draft: true,
        submission_payment_status: PAYMENT_STATUS.PENDING
      };

      await adminApi.updateFair(Number(fairId), updatedData);
      setFormData(updatedData);
      showSuccess('Fair unpublished successfully');
    } catch (err) {
      const apiError = extractApiError(err);
      showError(`Failed to unpublish fair: ${apiError.message}`);
    } finally {
      setPublishLoading(false);
    }
  };

  // Image management functions
  const addImage = async () => {
    if (!fairId) return;
    if (!newImageUrl.trim()) {
      setError('Please enter an image URL');
      return;
    }

    // Client-side URL validation
    try {
      const url = new URL(newImageUrl.trim());
      if (!['http:', 'https:'].includes(url.protocol)) {
        setError('Image URL must use HTTP or HTTPS protocol');
        return;
      }
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com/image.jpg)');
      return;
    }

    try {
      const imageData: FairImageCreate = {
        image_url: newImageUrl.trim(),
        caption: newImageCaption.trim() || undefined,
        display_order: images.length
      };
      await adminApi.createFairImage(Number(fairId), imageData);
      await loadImages(Number(fairId));
      setNewImageUrl('');
      setNewImageCaption('');
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to add image:', err);

      const errorDetail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;

      // Map backend errors to user-friendly messages
      let userMessage = 'Failed to add image';
      if (errorDetail) {
        if (errorDetail.includes('Invalid image URL')) {
          userMessage = 'Please enter a valid image URL starting with http:// or https://';
        } else if (errorDetail.includes('Farm stand cannot have more than')) {
          userMessage = 'Maximum of 20 images reached. Please delete an image first.';
        } else if (errorDetail.includes('Fair not found')) {
          userMessage = 'Fair not found. Please refresh the page.';
        }
        // Don't expose other backend errors
      }

      setError(userMessage);
    }
  };

  const updateImage = async (imageId: number, updates: FairImageUpdate) => {
    if (!fairId) return;

    try {
      await adminApi.updateFairImage(Number(fairId), imageId, updates);
      await loadImages(Number(fairId));
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to update image:', err);
      setError('Failed to update image');
    }
  };

  const deleteImage = async (imageId: number) => {
    if (!fairId) return;

    const confirmed = await confirmAsync('Are you sure you want to delete this image?', {
      description: 'This action cannot be undone.',
      confirmLabel: 'Delete Image',
      cancelLabel: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await adminApi.deleteFairImage(Number(fairId), imageId);
      await loadImages(Number(fairId));
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to delete image:', err);
      setError('Failed to delete image');
    }
  };

  const moveImageUp = async (index: number) => {
    if (index === 0) return;
    const reorderedImages = [...images];
    [reorderedImages[index], reorderedImages[index - 1]] = [reorderedImages[index - 1], reorderedImages[index]];
    await reorderImages(reorderedImages);
  };

  const moveImageDown = async (index: number) => {
    if (index === images.length - 1) return;
    const reorderedImages = [...images];
    [reorderedImages[index], reorderedImages[index + 1]] = [reorderedImages[index + 1], reorderedImages[index]];
    await reorderImages(reorderedImages);
  };

  const reorderImages = async (reorderedImages: FairImage[]) => {
    if (!fairId) return;

    try {
      const imageIds = reorderedImages.map(img => img.id);
      await adminApi.reorderFairImages(Number(fairId), imageIds);
      await loadImages(Number(fairId));
      setError(null);
    } catch (err: unknown) {
      console.error('Failed to reorder images:', err);
      setError('Failed to reorder images');
    }
  };

  if (loading && isEdit) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading artist fair...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {isEdit ? 'Edit Artist Fair' : 'Add New Artist Fair'}
        </h1>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Publishing Status Banner (Issue #1670) */}
        {isEdit && authApi.isAdmin() && formData.is_draft && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h3 className="font-semibold text-yellow-900 flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  This fair is not published
                </h3>
                <p className="text-sm text-yellow-700 mt-1">
                  The fair is in draft mode and not visible to the public. Publish it to make it visible on the main page.
                </p>
                <p className="text-xs text-yellow-600 mt-1">
                  Status: <span className="font-medium">{formData.submission_payment_status}</span>
                </p>
              </div>
              <button
                type="button"
                onClick={handlePublish}
                disabled={publishLoading}
                className="flex-shrink-0 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium shadow-sm disabled:bg-green-400 disabled:cursor-not-allowed"
              >
                {publishLoading ? 'Publishing...' : 'Publish Fair (Waive Fee)'}
              </button>
            </div>
          </div>
        )}

        {/* Published Status Indicator */}
        {isEdit && authApi.isAdmin() && !formData.is_draft && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-green-900">
                  ✓ Fair is published
                </h3>
                <p className="text-sm text-green-700 mt-1">
                  This fair is visible to the public on the main page.
                </p>
                {formData.published_at && (
                  <p className="text-xs text-green-600 mt-1">
                    Published: {new Date(formData.published_at).toLocaleString()}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={handleUnpublish}
                disabled={publishLoading}
                className="flex-shrink-0 bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors font-medium shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {publishLoading ? 'Unpublishing...' : 'Unpublish Fair'}
              </button>
            </div>
          </div>
        )}

        {/* Mobile Dropdown Menu (< lg screens) */}
        <div className="lg:hidden mb-6">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center space-x-2">
              <Menu className="h-5 w-5 text-gray-500" />
              <span className="font-medium text-gray-700">
                {activeTab === 'basic' && 'Basic Information'}
                {activeTab === 'images' && 'Images'}
              </span>
            </div>
            {mobileMenuOpen ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {mobileMenuOpen && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
              <button
                type="button"
                onClick={() => { setActiveTab('basic'); setMobileMenuOpen(false); }}
                className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                  activeTab === 'basic' ? 'bg-teal-50 text-primary-dark font-medium' : 'text-gray-700'
                }`}
              >
                Basic Information
              </button>
              {isEdit && (
                <button
                  type="button"
                  onClick={() => { setActiveTab('images'); setMobileMenuOpen(false); }}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center justify-between ${
                    activeTab === 'images' ? 'bg-teal-50 text-primary-dark font-medium' : 'text-gray-700'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <ImageIcon className="h-4 w-4" />
                    <span>Images</span>
                  </div>
                  {images.length > 0 && (
                    <span className="bg-teal-100 text-primary-dark text-xs font-medium px-2 py-0.5 rounded-full">
                      {images.length}
                    </span>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Desktop Tab Navigation (>= lg screens) */}
        <div className="hidden lg:block border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              type="button"
              title="View and edit basic artist fair information"
              onClick={() => setActiveTab('basic')}
              className={`${
                activeTab === 'basic'
                  ? 'border-primary text-secondary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
            >
              Basic Information
            </button>
            {isEdit && (
              <button
                type="button"
                title="Manage artist fair images"
                onClick={() => setActiveTab('images')}
                className={`${
                  activeTab === 'images'
                    ? 'border-primary text-secondary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center space-x-2`}
              >
                <ImageIcon className="h-4 w-4" />
                <span>Images</span>
                {images.length > 0 && (
                  <span className="bg-teal-100 text-primary-dark text-xs font-medium px-2 py-0.5 rounded-full">
                    {images.length}
                  </span>
                )}
              </button>
            )}
          </nav>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
          <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Artist Fair Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                required
                maxLength={200}
                title="Enter the name of your artist fair (max 200 characters)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="fair-form-name-input"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                required
                maxLength={254}
                title="Enter your artist fair contact email address"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                data-testid="fair-form-email-input"
              />
            </div>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              title="Provide a description of your artist fair and what you offer"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              data-testid="fair-form-description-textarea"
            />
          </div>

          {/* Image Management Notice - Issue #359, #1373 */}
          <div className="bg-moonbone border border-teal-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <ImageIcon className="h-6 w-6 text-secondary flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-ink mb-1">
                  Image Management
                </h4>
                {isEdit ? (
                  <>
                    <p className="text-sm text-primary-dark mb-2">
                      All fair images are now managed in the <strong>Images</strong> tab above.
                      Upload and organize your images there, and the first image will automatically
                      be used as your main fair image.
                    </p>
                    <button
                      type="button"
                      onClick={() => setActiveTab('images')}
                      className="inline-flex items-center space-x-2 px-3 py-1.5 bg-primary text-white text-sm rounded hover:bg-primary-dark transition-colors"
                    >
                      <ImageIcon className="h-4 w-4" />
                      <span>Go to Images Tab</span>
                    </button>
                  </>
                ) : (
                  <p className="text-sm text-primary-dark">
                    After creating this fair, you'll be able to add and manage fair images in the <strong>Images</strong> tab.
                    The first image you upload will automatically be used as your main fair image.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                title="Enter your artist fair phone number"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                data-testid="fair-form-phone-input"
              />
            </div>
            
            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                Website
              </label>
              <input
                id="website"
                type="url"
                title="Enter your artist fair website URL"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.website}
                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                data-testid="fair-form-website-input"
              />
            </div>
          </div>

          {/* Multi-Day Event Scheduling (Issue #11) */}
          <div className="col-span-2 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Schedule</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Start Date */}
              <div>
                <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Event Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="start_date"
                  type="date"
                  required
                  title="Select the event start date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.start_date}
                  onChange={(e) => {
                    const newStartDate = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      start_date: newStartDate,
                      // Auto-adjust end_date if it's before the new start_date
                      end_date: prev.end_date && prev.end_date < newStartDate ? newStartDate : prev.end_date
                    }));
                  }}
                  data-testid="fair-form-start-date-input"
                />
              </div>

              {/* End Date */}
              <div>
                <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                  Event End Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="end_date"
                  type="date"
                  required
                  title="Select the event end date (must be same or after start date)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.end_date}
                  min={formData.start_date || undefined}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  data-testid="fair-form-end-date-input"
                />
                {formData.start_date && formData.end_date && (
                  <p className="text-xs text-gray-600 mt-1">
                    Duration: {(() => {
                      const start = parseDateOnly(formData.start_date);
                      const end = parseDateOnly(formData.end_date);
                      if (!start || !end) return '';
                      const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                      return days === 1 ? '1 day' : `${days} days`;
                    })()}
                  </p>
                )}
              </div>

              {/* Total Slots */}
              <div>
                <label htmlFor="total_slots" className="block text-sm font-medium text-gray-700 mb-2">
                  Total Vendor Booth Spaces <span className="text-red-500">*</span>
                </label>
                <input
                  id="total_slots"
                  type="number"
                  min="0"
                  required
                  title="Enter the total number of vendor booth spaces available"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.total_slots}
                  onChange={(e) => setFormData(prev => ({ ...prev, total_slots: parseInt(e.target.value) || 0 }))}
                  data-testid="fair-form-total-slots-input"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Number of booth spaces available for vendor reservations
                </p>
              </div>

              {/* Allows Per-Day Reservation */}
              <div className="flex items-start space-x-3 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <input
                  id="allows_per_day_reservation"
                  type="checkbox"
                  title="Allow vendors to reserve booth spaces for specific days instead of the full event duration"
                  className="mt-1 h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  checked={formData.allows_per_day_reservation}
                  onChange={(e) => setFormData(prev => ({ ...prev, allows_per_day_reservation: e.target.checked }))}
                  data-testid="fair-form-allows-per-day-reservation-checkbox"
                />
                <div>
                  <label htmlFor="allows_per_day_reservation" className="text-sm font-medium text-gray-900 block">
                    Allow Per-Day Reservations
                  </label>
                  <span className="text-xs text-gray-600">
                    Let vendors book booth spaces for specific days instead of the full event duration
                  </span>
                </div>
              </div>

              {/* Slot Offer Duration (Issue #640, #707) */}
              <div>
                <label htmlFor="offer_duration_hours" className="block text-sm font-medium text-gray-700 mb-2">
                  Slot Offer Duration (Hours) <span className="text-red-500">*</span>
                  <Tooltip
                    content="Choose shorter durations for high-demand fairs to keep waitlists moving quickly. Longer durations work better for specialty events where vendors need more time to decide."
                    position="right"
                  >
                    <Info className="inline-block ml-1 w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                  </Tooltip>
                </label>
                <input
                  id="offer_duration_hours"
                  type="number"
                  min="1"
                  max="168"
                  required
                  title="How long vendors have to accept slot offers (1-168 hours)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.offer_duration_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, offer_duration_hours: parseInt(e.target.value) || 48 }))}
                  data-testid="fair-form-offer-duration-hours-input"
                />

                {/* Preset duration buttons (Issue #707) */}
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, offer_duration_hours: 24 }))}
                    aria-label="Set offer duration to 24 hours for high-demand fairs"
                    className={`text-xs px-3 py-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      formData.offer_duration_hours === 24
                        ? 'bg-teal-100 text-primary-dark border border-teal-300'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    24h (Fast)
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, offer_duration_hours: 48 }))}
                    aria-label="Set offer duration to 48 hours for standard fairs"
                    className={`text-xs px-3 py-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      formData.offer_duration_hours === 48
                        ? 'bg-teal-100 text-primary-dark border border-teal-300'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    48h (Standard)
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, offer_duration_hours: 72 }))}
                    aria-label="Set offer duration to 72 hours for specialty fairs"
                    className={`text-xs px-3 py-1 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 ${
                      formData.offer_duration_hours === 72
                        ? 'bg-teal-100 text-primary-dark border border-teal-300'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    72h (Relaxed)
                  </button>
                </div>

                {/* Enhanced help text (Issue #707) */}
                <p className="text-xs text-gray-600 mt-2">
                  How long vendors have to accept slot offers from the waitlist (1-168 hours).
                  <br />
                  <strong className="text-gray-700">Recommended:</strong> 24h for high-demand urban fairs, 48h for standard fairs, 72h+ for specialty craft/art fairs.
                </p>
              </div>
            </div>
          </div>

          {/* Fair Amenities & Features (Issue #54) */}
          <div className="col-span-2 border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Amenities & Features</h3>
            <p className="text-sm text-gray-600 mb-4">
              Select the amenities and features available at this fair to help vendors and attendees know what to expect.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Live Music */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                <input
                  id="has_live_music"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                  checked={formData.has_live_music || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, has_live_music: e.target.checked }))}
                  data-testid="fair-form-has-live-music-checkbox"
                />
                <div>
                  <label htmlFor="has_live_music" className="text-sm font-medium text-gray-900 block cursor-pointer">
                    🎵 Live Music
                  </label>
                  <span className="text-xs text-gray-600">
                    Live music performances during the fair
                  </span>
                </div>
              </div>

              {/* Beer & Wine / Alcohol */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                <input
                  id="has_alcohol"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                  checked={formData.has_alcohol || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, has_alcohol: e.target.checked }))}
                  data-testid="fair-form-has-alcohol-checkbox"
                />
                <div>
                  <label htmlFor="has_alcohol" className="text-sm font-medium text-gray-900 block cursor-pointer">
                    🍺 Beer & Wine / Alcohol
                  </label>
                  <span className="text-xs text-gray-600">
                    Beer, wine, or alcohol sales permitted/available
                  </span>
                </div>
              </div>

              {/* Guest Appearances */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                <input
                  id="has_guest_appearances"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                  checked={formData.has_guest_appearances || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, has_guest_appearances: e.target.checked }))}
                  data-testid="fair-form-has-guest-appearances-checkbox"
                />
                <div>
                  <label htmlFor="has_guest_appearances" className="text-sm font-medium text-gray-900 block cursor-pointer">
                    ⭐ Guest Appearances
                  </label>
                  <span className="text-xs text-gray-600">
                    Special guest appearances or celebrity visits
                  </span>
                </div>
              </div>

              {/* Giveaways & Raffles */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                <input
                  id="has_giveaways"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                  checked={formData.has_giveaways || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, has_giveaways: e.target.checked }))}
                  data-testid="fair-form-has-giveaways-checkbox"
                />
                <div>
                  <label htmlFor="has_giveaways" className="text-sm font-medium text-gray-900 block cursor-pointer">
                    🎁 Giveaways & Raffles
                  </label>
                  <span className="text-xs text-gray-600">
                    Giveaways, raffles, or prize drawings
                  </span>
                </div>
              </div>

              {/* Food Trucks */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                <input
                  id="has_food_trucks"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                  checked={formData.has_food_trucks || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, has_food_trucks: e.target.checked }))}
                  data-testid="fair-form-has-food-trucks-checkbox"
                />
                <div>
                  <label htmlFor="has_food_trucks" className="text-sm font-medium text-gray-900 block cursor-pointer">
                    🚚 Food Trucks
                  </label>
                  <span className="text-xs text-gray-600">
                    Food trucks or food vendors on-site
                  </span>
                </div>
              </div>

              {/* Affiliated Event */}
              <div className="flex items-start space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                <input
                  id="is_affiliated"
                  type="checkbox"
                  className="mt-1 h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                  checked={formData.is_affiliated || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_affiliated: e.target.checked }))}
                  data-testid="fair-form-is-affiliated-checkbox"
                />
                <div>
                  <label htmlFor="is_affiliated" className="text-sm font-medium text-gray-900 block cursor-pointer">
                    🤝 Affiliated Event
                  </label>
                  <span className="text-xs text-gray-600">
                    Fair is affiliated with a larger organization or event
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Attendee Information (Issue #150) */}
          <div className="space-y-4 bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendee Information</h3>

            {/* Fair Categories */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fair Categories
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {['craft fair', 'farmers market', 'art show', 'holiday market', 'music festival', 'food festival', 'flea market', 'vintage market', 'street fair', 'community event'].map((category) => (
                  <label key={category} className="flex items-center space-x-2 p-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 cursor-pointer">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                      checked={formData.fair_categories.includes(category)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, fair_categories: [...prev.fair_categories, category] }));
                        } else {
                          setFormData(prev => ({ ...prev, fair_categories: prev.fair_categories.filter(c => c !== category) }));
                        }
                      }}
                      data-testid={`fair-form-category-${category.replace(/ /g, '-')}-checkbox`}
                    />
                    <span className="text-sm text-gray-900 capitalize">{category}</span>
                  </label>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">Select all that apply</p>
            </div>
          </div>

          {/* Policies & Rules (Issue #150) - Collapsible */}
          <div className="space-y-4 bg-white p-6 rounded-lg border border-gray-200">
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setPoliciesExpanded(!policiesExpanded)}
            >
              <h3 className="text-lg font-semibold text-gray-900">Policies & Rules</h3>
              {policiesExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-500" />
              )}
            </div>

            {policiesExpanded && (
              <div className="space-y-4 pt-4">
                {/* Prohibited Items */}
                <div>
                  <label htmlFor="prohibited_items" className="block text-sm font-medium text-gray-700 mb-1">
                    Prohibited Items
                  </label>
                  <textarea
                    id="prohibited_items"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Weapons, live animals, alcohol, illegal substances"
                    value={formData.prohibited_items}
                    onChange={(e) => setFormData(prev => ({ ...prev, prohibited_items: e.target.value }))}
                  />
                  <p className="mt-1 text-xs text-gray-500">Items that cannot be sold at this fair</p>
                </div>

                {/* Sales Tax Information */}
                <div>
                  <label htmlFor="sales_tax_info" className="block text-sm font-medium text-gray-700 mb-1">
                    Sales Tax Information
                  </label>
                  <textarea
                    id="sales_tax_info"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Vendors are responsible for collecting and remitting sales tax. State tax ID required."
                    value={formData.sales_tax_info}
                    onChange={(e) => setFormData(prev => ({ ...prev, sales_tax_info: e.target.value }))}
                  />
                  <p className="mt-1 text-xs text-gray-500">Vendor sales tax responsibilities</p>
                </div>

                {/* Photography Policy */}
                <div>
                  <label htmlFor="photography_policy" className="block text-sm font-medium text-gray-700 mb-1">
                    Photography Policy
                  </label>
                  <textarea
                    id="photography_policy"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="We may photograph vendors and products for promotional use. Vendors will be notified and can opt out."
                    value={formData.photography_policy}
                    onChange={(e) => setFormData(prev => ({ ...prev, photography_policy: e.target.value }))}
                  />
                  <p className="mt-1 text-xs text-gray-500">Can fair photograph vendors/products for marketing?</p>
                </div>
              </div>
            )}
          </div>

          {/* Test Artist Fair Toggle (Admin Only) */}
          {authApi.isAdmin() && (
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <input
                id="is_test"
                type="checkbox"
                title="Mark this artist fair as a test entry (for development/testing purposes)"
                className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
                checked={formData.is_test}
                onChange={(e) => setFormData(prev => ({ ...prev, is_test: e.target.checked }))}
                data-testid="fair-form-is-test-checkbox"
              />
              <label htmlFor="is_test" className="text-sm font-medium text-gray-900">
                Test Artist Fair
              </label>
              <span className="text-xs text-gray-600">
                (Mark as test entry for development/testing)
              </span>
            </div>
          )}

          {/* Featured Artist Fair Toggle (Admin Only) */}
          {authApi.isAdmin() && (
            <>
              <div className="flex items-center space-x-3 p-4 bg-moonbone border border-teal-200 rounded-lg">
                <input
                  id="is_featured"
                  type="checkbox"
                  title="Feature this artist fair (only one can be featured per city)"
                  className="h-4 w-4 text-secondary focus:ring-dusty-teal border-gray-300 rounded"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                  data-testid="fair-form-is-featured-checkbox"
                />
                <label htmlFor="is_featured" className="text-sm font-medium text-gray-900">
                  Featured Artist Fair
                </label>
                <span className="text-xs text-gray-600">
                  (Pin to top of list - only one per city)
                </span>
              </div>

              {/* Founding Market Badge (Issue #1318) */}
              <div className="flex items-center space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <input
                  id="is_founding_market"
                  type="checkbox"
                  title="Mark this fair as a founding market (early-adopter recognition)"
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                  checked={formData.is_founding_market}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_founding_market: e.target.checked }))}
                  data-testid="fair-form-is-founding-market-checkbox"
                />
                <label htmlFor="is_founding_market" className="text-sm font-medium text-gray-900">
                  Founding Market Badge
                </label>
                <span className="text-xs text-gray-600">
                  (Recognize early-adopter markets)
                </span>
              </div>
            </>
          )}

          {/* Has Attendant Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Attendant Status
            </label>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="has_attendant"
                  title="Farm stand has an attendant present"
                  checked={formData.has_attendant === true}
                  onChange={() => setFormData(prev => ({ ...prev, has_attendant: true }))}
                  className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
                  data-testid="fair-form-has-attendant-attended-radio"
                />
                <span className="text-sm text-gray-700">Attended</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="has_attendant"
                  title="Farm stand is self-serve (no attendant)"
                  checked={formData.has_attendant === false}
                  onChange={() => setFormData(prev => ({ ...prev, has_attendant: false }))}
                  className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
                  data-testid="fair-form-has-attendant-self-serve-radio"
                />
                <span className="text-sm text-gray-700">Self-Serve</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="radio"
                  name="has_attendant"
                  title="Attendant status is unknown"
                  checked={formData.has_attendant === null}
                  onChange={() => setFormData(prev => ({ ...prev, has_attendant: null }))}
                  className="h-4 w-4 text-secondary focus:ring-primary border-gray-300"
                  data-testid="fair-form-has-attendant-unknown-radio"
                />
                <span className="text-sm text-gray-700">Unknown</span>
              </label>
            </div>
            <p className="text-xs text-gray-500">
              Indicate whether the artist fair has an attendant present or is self-serve
            </p>
          </div>

          {/* Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Address</h3>
            
            <div>
              <label htmlFor="street" className="block text-sm font-medium text-gray-700 mb-2">
                Street Address
              </label>
              <input
                id="street"
                type="text"
                title="Enter the street address of your artist fair"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                value={formData.address.street}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  address: { ...prev.address, street: e.target.value }
                }))}
                data-testid="fair-form-street-input"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                  City
                </label>
                <input
                  id="city"
                  type="text"
                  title="Enter the city where your artist fair is located"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.address.city}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, city: e.target.value }
                  }))}
                  data-testid="fair-form-city-input"
                />
              </div>
              
              <div>
                <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                  State
                </label>
                <input
                  id="state"
                  type="text"
                  title="Enter the state where your artist fair is located"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.address.state}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, state: e.target.value }
                  }))}
                  data-testid="fair-form-state-input"
                />
              </div>
              
              <div>
                <label htmlFor="zip" className="block text-sm font-medium text-gray-700 mb-2">
                  ZIP Code
                </label>
                <input
                  id="zip"
                  type="text"
                  title="Enter the ZIP code for your artist fair location"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.address.zip}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    address: { ...prev.address, zip: e.target.value }
                  }))}
                  data-testid="fair-form-zip-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="latitude" className="block text-sm font-medium text-gray-700 mb-2">
                  Latitude
                </label>
                <input
                  id="latitude"
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g., 42.3601"
                  title="Enter the latitude coordinate for your artist fair location (range: -90 to +90)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={latInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLatInput(value); // Update input state immediately

                    // Update formData with number when valid, or 0 if empty
                    const numValue = parseFloat(value);
                    if (value === '') {
                      setFormData(prev => ({
                        ...prev,
                        address: { ...prev.address, coordinates: { ...prev.address.coordinates, lat: 0 } }
                      }));
                    } else if (!isNaN(numValue)) {
                      setFormData(prev => ({
                        ...prev,
                        address: { ...prev.address, coordinates: { ...prev.address.coordinates, lat: numValue } }
                      }));
                    }
                    // If intermediate value like "-" or ".", just update the input, don't update formData
                  }}
                  data-testid="fair-form-latitude-input"
                />
              </div>

              <div>
                <label htmlFor="longitude" className="block text-sm font-medium text-gray-700 mb-2">
                  Longitude
                </label>
                <input
                  id="longitude"
                  type="text"
                  inputMode="decimal"
                  placeholder="e.g., -71.0589"
                  title="Enter the longitude coordinate for your artist fair location (range: -180 to +180)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={lngInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLngInput(value); // Update input state immediately

                    // Update formData with number when valid, or 0 if empty
                    const numValue = parseFloat(value);
                    if (value === '') {
                      setFormData(prev => ({
                        ...prev,
                        address: { ...prev.address, coordinates: { ...prev.address.coordinates, lng: 0 } }
                      }));
                    } else if (!isNaN(numValue)) {
                      setFormData(prev => ({
                        ...prev,
                        address: { ...prev.address, coordinates: { ...prev.address.coordinates, lng: numValue } }
                      }));
                    }
                    // If intermediate value like "-" or ".", just update the input, don't update formData
                  }}
                  data-testid="fair-form-longitude-input"
                />
              </div>
            </div>

            {/* Derive Address Button */}
            <div className="mt-4">
              <button
                type="button"
                onClick={deriveAddressFromCoordinates}
                disabled={geocoding || !formData.address.coordinates.lat || !formData.address.coordinates.lng}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {geocoding ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    <span>Deriving Address...</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4" />
                    <span>Derive Address from Coordinates</span>
                  </>
                )}
              </button>
              {error && error.includes('coordinate') && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
              )}
            </div>
          </div>

          {/* Social Media */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Social Media</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-2">
                  Facebook Username
                </label>
                <input
                  id="facebook"
                  type="text"
                  placeholder="username (without @)"
                  title="Enter your artist fair's Facebook username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.social.facebook}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social: { ...prev.social, facebook: e.target.value }
                  }))}
                  data-testid="fair-form-facebook-input"
                />
              </div>
              
              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
                  Instagram Username
                </label>
                <input
                  id="instagram"
                  type="text"
                  placeholder="username (without @)"
                  title="Enter your artist fair's Instagram username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.social.instagram}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social: { ...prev.social, instagram: e.target.value }
                  }))}
                  data-testid="fair-form-instagram-input"
                />
              </div>
              
              <div>
                <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-2">
                  Twitter Username
                </label>
                <input
                  id="twitter"
                  type="text"
                  placeholder="username (without @)"
                  title="Enter your artist fair's Twitter/X username"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.social.twitter}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    social: { ...prev.social, twitter: e.target.value }
                  }))}
                  data-testid="fair-form-twitter-input"
                />
              </div>
            </div>
          </div>

          {/* Operational Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Operational Details</h3>
            <p className="text-sm text-gray-600">Provide practical information for vendors about your event operations.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="daily_hours" className="block text-sm font-medium text-gray-700 mb-2">
                  Daily Hours
                  <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                </label>
                <input
                  id="daily_hours"
                  type="text"
                  placeholder="9:00 AM - 5:00 PM"
                  title="Operating hours for each day of the fair"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.daily_hours}
                  onChange={(e) => setFormData(prev => ({ ...prev, daily_hours: e.target.value }))}
                  data-testid="fair-form-daily-hours-input"
                />
                <p className="text-xs text-gray-500 mt-1">Example: "9:00 AM - 5:00 PM" or "10-6"</p>
              </div>

              <div>
                <label htmlFor="is_indoor" className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    id="is_indoor"
                    type="checkbox"
                    className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                    checked={formData.is_indoor}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_indoor: e.target.checked }))}
                    data-testid="fair-form-is-indoor-checkbox"
                  />
                  <span>Indoor Event</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">Check if this is primarily an indoor fair</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="load_in_time" className="block text-sm font-medium text-gray-700 mb-2">
                  Load-In Time
                  <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                </label>
                <input
                  id="load_in_time"
                  type="time"
                  title="When vendors can arrive to set up their booths"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.load_in_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, load_in_time: e.target.value }))}
                  data-testid="fair-form-load-in-time-input"
                />
                <p className="text-xs text-gray-500 mt-1">When vendors can start setting up</p>
              </div>

              <div>
                <label htmlFor="load_out_time" className="block text-sm font-medium text-gray-700 mb-2">
                  Load-Out Time
                  <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                </label>
                <input
                  id="load_out_time"
                  type="time"
                  title="When vendors can begin packing up after the fair"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.load_out_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, load_out_time: e.target.value }))}
                  data-testid="fair-form-load-out-time-input"
                />
                <p className="text-xs text-gray-500 mt-1">When vendors can start packing up</p>
              </div>
            </div>

            <div>
              <label htmlFor="parking_info" className="block text-sm font-medium text-gray-700 mb-2">
                Parking Information
                <span className="text-gray-500 font-normal ml-2">(Optional)</span>
              </label>
              <textarea
                id="parking_info"
                rows={3}
                placeholder="Describe parking options for vendors and attendees..."
                title="Parking information for vendors and attendees"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={500}
                value={formData.parking_info}
                onChange={(e) => setFormData(prev => ({ ...prev, parking_info: e.target.value }))}
                data-testid="fair-form-parking-info-textarea"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.parking_info.length}/500 characters - Include vendor loading zones, attendee parking, fees, etc.
              </p>
            </div>

            <div>
              <label htmlFor="weather_policy" className="block text-sm font-medium text-gray-700 mb-2">
                Weather Policy
                <span className="text-gray-500 font-normal ml-2">(Optional)</span>
              </label>
              <textarea
                id="weather_policy"
                rows={3}
                placeholder="What happens if it rains? Cancellation/refund policy for inclement weather..."
                title="Policy for handling bad weather conditions"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={1000}
                value={formData.weather_policy}
                onChange={(e) => setFormData(prev => ({ ...prev, weather_policy: e.target.value }))}
                data-testid="fair-form-weather-policy-textarea"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.weather_policy.length}/1000 characters - Cancellation, rescheduling, or rain-or-shine policies
              </p>
            </div>
          </div>

          {/* Attendee Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Attendee Information</h3>
            <p className="text-sm text-gray-600">Help vendors understand your audience and event details.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="attendee_entry_fee" className="block text-sm font-medium text-gray-700 mb-2">
                  Attendee Entry Fee
                  <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                  <input
                    id="attendee_entry_fee"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    title="Entry fee charged to attendees (0 = free)"
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    value={formData.attendee_entry_fee || ''}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        attendee_entry_fee: isNaN(value) || value < 0 ? 0 : value
                      }));
                    }}
                    data-testid="fair-form-attendee-entry-fee-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">$0.00 = Free admission for attendees</p>
              </div>

              <div>
                <label htmlFor="expected_attendance" className="block text-sm font-medium text-gray-700 mb-2">
                  Expected Attendance
                  <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                </label>
                <input
                  id="expected_attendance"
                  type="number"
                  min="0"
                  placeholder="1000"
                  title="Estimated number of attendees"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.expected_attendance || ''}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      expected_attendance: isNaN(value) || value < 0 ? 0 : value
                    }));
                  }}
                  data-testid="fair-form-expected-attendance-input"
                />
                <p className="text-xs text-gray-500 mt-1">Approximate number of visitors expected</p>
              </div>
            </div>
          </div>

          {/* Vendor Requirements */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Vendor Requirements</h3>
            <p className="text-sm text-gray-600">Important requirements and details for vendors applying to your fair.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="insurance_required" className="flex items-center space-x-2 text-sm font-medium text-gray-700">
                  <input
                    id="insurance_required"
                    type="checkbox"
                    className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
                    checked={formData.insurance_required}
                    onChange={(e) => setFormData(prev => ({ ...prev, insurance_required: e.target.checked }))}
                    data-testid="fair-form-insurance-required-checkbox"
                  />
                  <span>Liability Insurance Required</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">Check if vendors must have liability insurance</p>
              </div>

              <div>
                <label htmlFor="application_deadline" className="block text-sm font-medium text-gray-700 mb-2">
                  Application Deadline
                  <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                </label>
                <input
                  id="application_deadline"
                  type="date"
                  title="Last day vendors can apply to participate"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  value={formData.application_deadline}
                  max={formData.start_date || undefined}
                  onChange={(e) => setFormData(prev => ({ ...prev, application_deadline: e.target.value }))}
                  data-testid="fair-form-application-deadline-input"
                />
                <p className="text-xs text-gray-500 mt-1">Must be before the fair start date</p>
                {formData.application_deadline && formData.start_date && formData.application_deadline >= formData.start_date && (
                  <p className="text-xs text-red-600 mt-1">⚠️ Application deadline must be before the fair start date</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="booth_includes" className="block text-sm font-medium text-gray-700 mb-2">
                What's Included in Booth
                <span className="text-gray-500 font-normal ml-2">(Optional)</span>
              </label>
              <textarea
                id="booth_includes"
                rows={3}
                placeholder="Tables, chairs, tent, etc."
                title="Items/amenities included with booth rental"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={500}
                value={formData.booth_includes}
                onChange={(e) => setFormData(prev => ({ ...prev, booth_includes: e.target.value }))}
                data-testid="fair-form-booth-includes-textarea"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.booth_includes.length}/500 characters - List what vendors get with their booth
              </p>
            </div>

            <div>
              <label htmlFor="power_availability" className="block text-sm font-medium text-gray-700 mb-2">
                Fair-Wide Power Availability
                <span className="text-gray-500 font-normal ml-2">(Optional)</span>
              </label>
              <textarea
                id="power_availability"
                rows={3}
                placeholder="Describe power options, costs, limitations..."
                title="Information about electrical power availability for vendors"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                maxLength={500}
                value={formData.power_availability}
                onChange={(e) => setFormData(prev => ({ ...prev, power_availability: e.target.value }))}
                data-testid="fair-form-power-availability-textarea"
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.power_availability.length}/500 characters - Voltage, amps, extra fees, etc.
              </p>
            </div>
          </div>

          {/* Policies & Rules - Collapsible */}
          <div className="space-y-4">
            <div
              className="flex items-center justify-between cursor-pointer p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setPoliciesExpanded(!policiesExpanded)}
            >
              <div>
                <h3 className="text-lg font-medium text-gray-900">Policies & Rules</h3>
                <p className="text-sm text-gray-600">Optional policies and guidelines for vendors</p>
              </div>
              {policiesExpanded ? (
                <ChevronUp className="h-5 w-5 text-gray-600" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-600" />
              )}
            </div>

            {policiesExpanded && (
              <div className="space-y-4 pl-4">
                <div>
                  <label htmlFor="prohibited_items" className="block text-sm font-medium text-gray-700 mb-2">
                    Prohibited Items
                    <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                  </label>
                  <textarea
                    id="prohibited_items"
                    rows={3}
                    placeholder="List items vendors cannot sell at this fair..."
                    title="Items vendors are not allowed to sell"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    maxLength={1000}
                    value={formData.prohibited_items}
                    onChange={(e) => setFormData(prev => ({ ...prev, prohibited_items: e.target.value }))}
                    data-testid="fair-form-prohibited-items-textarea"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.prohibited_items.length}/1000 characters - E.g., weapons, alcohol, live animals
                  </p>
                </div>

                <div>
                  <label htmlFor="sales_tax_info" className="block text-sm font-medium text-gray-700 mb-2">
                    Sales Tax Information
                    <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                  </label>
                  <textarea
                    id="sales_tax_info"
                    rows={3}
                    placeholder="Information about sales tax requirements for vendors..."
                    title="Sales tax information for vendors"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    maxLength={1000}
                    value={formData.sales_tax_info}
                    onChange={(e) => setFormData(prev => ({ ...prev, sales_tax_info: e.target.value }))}
                    data-testid="fair-form-sales-tax-info-textarea"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.sales_tax_info.length}/1000 characters - Tax ID requirements, collection responsibilities, etc.
                  </p>
                </div>

                <div>
                  <label htmlFor="photography_policy" className="block text-sm font-medium text-gray-700 mb-2">
                    Photography Policy
                    <span className="text-gray-500 font-normal ml-2">(Optional)</span>
                  </label>
                  <textarea
                    id="photography_policy"
                    rows={3}
                    placeholder="Policy regarding photography at the fair..."
                    title="Photography and media policy"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    maxLength={1000}
                    value={formData.photography_policy}
                    onChange={(e) => setFormData(prev => ({ ...prev, photography_policy: e.target.value }))}
                    data-testid="fair-form-photography-policy-textarea"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.photography_policy.length}/1000 characters - Commercial photography, social media sharing, etc.
                  </p>
                </div>
              </div>
            )}
          </div>
          </>
          )}

          {/* Images Tab - Only show for existing fairs */}
          {activeTab === 'images' && isEdit && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">Artist Fair Images</h3>
              {images.length >= 20 && (
                <span className="text-sm text-red-600 font-medium">
                  Maximum of 20 images reached
                </span>
              )}
            </div>

            <p className="text-sm text-gray-600">
              Manage multiple images for your artist fair. Images will be displayed in a carousel on the artist fair detail page.
            </p>

            {/* Primary Image Info - Issue #359 */}
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-secondary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-teal-900 mb-1">
                    Main Fair Image
                  </h4>
                  <p className="text-sm text-primary-dark">
                    The <strong>first image</strong> in your gallery (Image 1) will be used as your
                    main fair image throughout the site. Use the reorder buttons to change which
                    image appears first.
                  </p>
                </div>
              </div>
            </div>

            {/* Add New Image Form */}
            <div className="bg-moonbone border border-teal-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-ink mb-3">Add New Image</h4>
              <div className="space-y-3">
                <div>
                  <label htmlFor="new-image-url" className="block text-sm font-medium text-gray-700 mb-2">
                    Image URL *
                  </label>
                  <input
                    id="new-image-url"
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    title="Enter the URL for the new image"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dusty-teal"
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    data-testid="fair-form-new-image-url-input"
                  />
                </div>
                <div>
                  <label htmlFor="new-image-caption" className="block text-sm font-medium text-gray-700 mb-2">
                    Caption (Optional)
                  </label>
                  <textarea
                    id="new-image-caption"
                    rows={2}
                    placeholder="Enter a caption for this image (max 500 characters)"
                    title="Enter an optional caption for the image"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-dusty-teal"
                    maxLength={500}
                    value={newImageCaption}
                    onChange={(e) => setNewImageCaption(e.target.value)}
                    data-testid="fair-form-new-image-caption-textarea"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {newImageCaption.length}/500 characters
                  </p>
                </div>
                <button
                  type="button"
                  onClick={addImage}
                  disabled={!newImageUrl.trim() || images.length >= 20}
                  title={images.length >= 20 ? "Maximum image limit reached" : "Add this image to the artist fair"}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="h-4 w-4 inline mr-2" />
                  Add Image
                </button>
              </div>
            </div>

            {/* Current Images List */}
            {loadingImages ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading images...</p>
              </div>
            ) : images.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-900">
                  Current Images ({images.length})
                </h4>
                {images.map((image, index) => (
                  <div key={image.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start space-x-4">
                      {/* Image Preview */}
                      <img
                        src={image.image_url}
                        alt={image.caption || `Farm stand image ${index + 1}`}
                        className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                      />

                      {/* Image Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-700">
                              Image {index + 1}
                            </span>
                            {index === 0 && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-primary-dark border border-teal-200">
                                Main Fair Image
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {/* Reorder Buttons */}
                            <button
                              type="button"
                              onClick={() => moveImageUp(index)}
                              disabled={index === 0}
                              title="Move image up in display order"
                              className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <ChevronUp className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => moveImageDown(index)}
                              disabled={index === images.length - 1}
                              title="Move image down in display order"
                              className="p-1 text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <ChevronDown className="h-5 w-5" />
                            </button>
                            {/* Delete Button */}
                            <button
                              type="button"
                              onClick={() => deleteImage(image.id)}
                              title="Delete this image"
                              className="p-1 text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </div>

                        {/* Image URL */}
                        <div className="mb-2">
                          <label htmlFor={`image-url-${image.id}`} className="block text-xs font-medium text-gray-600 mb-1">
                            URL
                          </label>
                          <input
                            id={`image-url-${image.id}`}
                            type="url"
                            value={image.image_url}
                            onChange={(e) => updateImage(image.id, { image_url: e.target.value })}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                            title="Edit the image URL"
                          />
                        </div>

                        {/* Caption */}
                        <div>
                          <label htmlFor={`image-caption-${image.id}`} className="block text-xs font-medium text-gray-600 mb-1">
                            Caption
                          </label>
                          <textarea
                            id={`image-caption-${image.id}`}
                            rows={2}
                            value={image.caption || ''}
                            onChange={(e) => updateImage(image.id, { caption: e.target.value })}
                            maxLength={500}
                            placeholder="Enter a caption (optional, max 500 characters)"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary"
                            title="Edit the image caption"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {(image.caption || '').length}/500 characters
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 mb-2">No images added yet</p>
                <p className="text-sm text-gray-400">
                  Add images to create a visual gallery for your artist fair
                </p>
              </div>
            )}
          </div>
          )}


          {/* Form Actions - Always visible */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              title="Cancel and return to dashboard"
              onClick={() => navigate('/admin/dashboard')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              data-testid="cancel-fair-form-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              title={isEdit ? 'Update artist fair information' : 'Create new artist fair'}
              disabled={loading || publishLoading}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50"
              data-testid="submit-fair-form-button"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Artist Fair' : 'Create Artist Fair'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}